import * as React from 'react';
import { IServerUserInfo } from '../../ts/models/IServerUserInfo';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { UserSettingsNotificationParams, UserSettingsNotificationType, UserSettingsNotification } from '../notifications/UserSettingsNotification';
import { getAppStore } from '../../app/AppStore';
import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { FormikHelpers, FormikProps, Formik, useFormik } from 'formik';
import { UserSettings } from '../services/resources/UserSettings';
import { ILanguage } from '../../ts/ServerApi/types/ILanguage';
import { LanguagesInstance } from '../../ts/resources/Languages';
import { ListItem, List, DropdownButtonType } from '@tableau/widgets';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { FormikSingleSelectDropdown } from '../../commonInfra/components/FormikSingleSelectDropdown';
import { style } from 'typestyle';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';
import { Messages } from '../../codegen/Localize';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';

export interface LanguageFormFields {
  language: string;
}

export type LanguageFormProps = {
  initialValues: LanguageFormFields;
  serverUser: IServerUserInfo;
}

const userIsViewingTheirOwnSettings = (sessionManagementService: SessionManagementService, serverUser: IServerUserInfo) => {
  return sessionManagementService.sessionUsername() === serverUser.username
    && sessionManagementService.sessionUserDomainName() === serverUser.domainName;
};

const notifyLanguageChange = (result: IResult, username: string) => {
  const userSettingsNotificationParams: UserSettingsNotificationParams = {
    result: result,
    username: username,
    userSettingsNotificationType: UserSettingsNotificationType.ChangeLanguage
  };
  UserSettingsNotification.notify(userSettingsNotificationParams);
};

async function showLanguageNotification(result: IResult, serverUser: IServerUserInfo) {
  const sessionManagementService = getSessionManagementServiceInstance();
  if (result && userIsViewingTheirOwnSettings(sessionManagementService, serverUser)) {
    await sessionManagementService.refreshSessionInfo();
    getAppStore().dispatch(incrementRouterSpaceReactKey());
    notifyLanguageChange(result, serverUser.username);
  } else if (result) {
    notifyLanguageChange(result, serverUser.username);
  }
}

function showLanguageButton(formik: FormikProps<LanguageFormFields>, initialLanguage: string): boolean {
  if (!formik.dirty || !formik.isValid || formik.isSubmitting || (formik.values.language.trim() === initialLanguage)) {
    return false;
  }
  return true;
}

const unspecifiedLanguageItemValue = 'UnspecifiedLanguage';
export function LanguageForm(props: LanguageFormProps): JSX.Element {
  const [items, setItems] = React.useState([]);
  const [usersCurrentLanguage, setUsersCurrentLanguage] = React.useState('');

  React.useEffect(() => {
    async function fetchLanguages() {
      let languages: ILanguage[] = await LanguagesInstance.getAll();
      languages.unshift(LanguagesInstance.unspecifiedLanguage());
      const newItems: ListItem[] = languages.map((language: ILanguage) => ({
        content: language.displayName,
        value: language.language ? language.language : unspecifiedLanguageItemValue,
        testId: language.language ? language.language : unspecifiedLanguageItemValue,
      }));
      setItems(newItems);
      const usersCurrentLanguage = props.initialValues.language ? props.initialValues.language : unspecifiedLanguageItemValue;
      setUsersCurrentLanguage(usersCurrentLanguage);
    }
    if (items.length === 0) {
      fetchLanguages();
    }
  });

  const handleSubmit = React.useCallback(async (language: string, formik: FormikHelpers<LanguageFormFields>) => {
    if (language !== usersCurrentLanguage) {
      let languageToUpdate = language;
      if (language === unspecifiedLanguageItemValue) {
        languageToUpdate = null;
      }
      const result: IResult = await UserSettings.updateUserLanguage(props.serverUser.id, languageToUpdate);
      showLanguageNotification(result, props.serverUser);
      if (!result || result.errors) {
        formik.resetForm();
      } else {
        setUsersCurrentLanguage(language);
      }
  }}, [props, setUsersCurrentLanguage]);

  return (
    <Formik
      initialValues={{ language: usersCurrentLanguage }}
      onSubmit={function(values: LanguageFormFields, formik: FormikHelpers<LanguageFormFields>) {
        handleSubmit(values.language, formik);
      }}
      enableReinitialize={true}
    >
      {(formik: FormikProps<LanguageFormFields>) => (
        <form onSubmit={formik.handleSubmit}>
          <table>
            <tbody className={UserSettingsStyle.tbodyNew}>
              {/* Language Section */}
              <tr data-test-id="language-section">
              <td className={UserSettingsStyle.labelTextNew}>
                  {Messages.language()}
              </td>
              <td className={UserSettingsStyle.detailsValueSmallPadding}>
                  <FormikSingleSelectDropdown
                      name="language"
                      list={<List items={items} className={UserSettingsStyle.dropdownList}/>}
                      type={DropdownButtonType.Outline}
                      label={''}
                      className={UserSettingsStyle.dropdown}
                      testId={'language'}/>
              </td>
              </tr>
              {(showLanguageButton(formik, usersCurrentLanguage) &&
                <InlineCancelAndSaveButtons
                testId={'edit-language'}
                formik={formik}
                />
              )}
            </tbody>
          </table>
          <div>
          </div>
        </form>
      )}
    </Formik>
  );
}
